import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import { createSignal } from 'solid-js'
import Badge from '~/components/Badge'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import Switch from '~/components/Switch'

const SwitchPage: Component = () => {
  const [wifi, setWifi] = createSignal(true)
  const [dark, setDark] = createSignal(false)

  return (
    <>
      <Title>Switch - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">Switch</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Control binario de alta precisión. El componente Switch permite alternar estados de configuración de manera instantánea, con soporte para iconos dinámicos, múltiples escalas y semántica de
            color extendida.
          </p>
        </div>

        <div class="space-y-12">
          {/* Configuración de Sistema */}
          <Card>
            <Card.Header
              title="Panel de Control"
              subtitle="Interacción fluida para ajustes en tiempo real con retroalimentación visual inmediata."
              icon={<Icon icon="material-symbols-light:tune" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-10 p-10 bg-surface-muted rounded-3xl border border-border border-dashed">
                <div class="space-y-6">
                  <h4 class="text-xs font-semibold text-foreground-subtle pl-3 border-l-4 border-info">Sistema</h4>
                  <div class="space-y-4">
                    <Switch
                      label="Notificaciones"
                      trueIcon="material-symbols-light:notifications-outline"
                      falseIcon="material-symbols-light:notifications-off-outline"
                      checked={wifi()}
                      onChange={e => setWifi(e.currentTarget.checked)}
                      color="info"
                    />
                    <Switch label="Sonido del sistema" trueIcon="material-symbols-light:volume-up-outline" falseIcon="material-symbols-light:volume-off-outline" checked color="success" />
                    <Switch label="Wi-Fi (deshabilitado)" trueIcon="material-symbols-light:router-outline" falseIcon="material-symbols-light:router-outline" checked disabled />
                  </div>
                </div>

                <div class="space-y-6">
                  <h4 class="text-xs font-semibold text-foreground-subtle pl-3 border-l-4 border-primary">Apariencia</h4>
                  <div class="space-y-4">
                    <Switch
                      label="Dark Mode UI"
                      trueIcon="material-symbols-light:dark-mode-outline"
                      falseIcon="material-symbols-light:sunny-outline"
                      checked={dark()}
                      onChange={e => setDark(e.currentTarget.checked)}
                      color="secondary"
                    />
                    <div class="p-3 bg-surface rounded-xl border border-border mt-4">
                      <div class="flex items-center justify-between">
                        <span class="text-[10px] font-mono text-foreground-subtle uppercase tracking-tighter">Current Theme Engine</span>
                        <Badge size="sm" color={dark() ? 'secondary' : 'warning'}>
                          {dark() ? 'DEEP_DARK' : 'VIVID_LIGHT'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Escalas y Colores Semánticos */}
          <Card>
            <Card.Header
              title="Variaciones y Escalas"
              subtitle="Cinco tamaños disponibles y paleta de colores completa para alineación estética."
              icon={<Icon icon="material-symbols-light:straighten-outline" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div class="space-y-6">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest">Dimensiones Atómicas</h4>
                  <div class="flex flex-col gap-6 items-start">
                    <div class="flex items-center gap-4">
                      <Switch size="sm" checked />
                      <span class="text-xs text-foreground-subtle font-bold uppercase tracking-widest">Extra Small</span>
                    </div>
                    <div class="flex items-center gap-4">
                      <Switch size="sm" checked />
                      <span class="text-xs text-foreground-subtle font-bold uppercase tracking-widest">Small</span>
                    </div>
                    <div class="flex items-center gap-4">
                      <Switch size="md" checked />
                      <span class="text-xs text-foreground-subtle font-bold uppercase tracking-widest">Medium (Default)</span>
                    </div>
                    <div class="flex items-center gap-4">
                      <Switch size="lg" checked />
                      <span class="text-xs text-foreground-subtle font-bold uppercase tracking-widest">Large</span>
                    </div>
                    <div class="flex items-center gap-4">
                      <Switch size="xl" checked />
                      <span class="text-xs text-foreground-subtle font-bold uppercase tracking-widest">Extra Large</span>
                    </div>
                  </div>
                </div>

                <div class="space-y-6">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest">Matriz de Color</h4>
                  <div class="grid grid-cols-2 sm:grid-cols-3 gap-6">
                    <Switch label="Success" color="success" checked />
                    <Switch label="Danger" color="danger" checked />
                    <Switch label="Warning" color="warning" checked />
                    <Switch label="Info" color="info" checked />
                    <Switch label="Secondary" color="secondary" checked />
                    <Switch label="Primary" color="primary" checked />
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Especificaciones técnicas para el componente Switch."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary-600">checked</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4 italic">Estado de activación del control.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">trueIcon / falseIcon</td>
                      <td class="px-6 py-4 italic text-xs">string (Iconify)</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4">Iconos renderizados sobre el toggle.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-secondary-600">size</td>
                      <td class="px-6 py-4 italic text-xs">xs | sm | md | lg | xl</td>
                      <td class="px-6 py-4 font-mono">'md'</td>
                      <td class="px-6 py-4 italic">Escala física del componente.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">tooltip</td>
                      <td class="px-6 py-4 italic text-xs">string</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic">Mensaje informativo al hover.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default SwitchPage
